import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import AppFooter from "@/components/AppFooter";
import { getPaymentStatus } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30; // ≈60s

const PLAN_LABELS = {
  pro_monthly: "PressImpact Pro (monthly)",
  pro_yearly: "PressImpact Pro (annual)",
  team_monthly: "PressImpact Team (monthly)",
  team_yearly: "PressImpact Team (annual)",
  agency_monthly: "PressImpact Agency (monthly)",
  agency_yearly: "PressImpact Agency (annual)",
};

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [lookupKey, setLookupKey] = useState(null);

  const poll = useCallback(
    async (attempt = 0) => {
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const data = await getPaymentStatus(sessionId);
        if (data.lookup_key) setLookupKey(data.lookup_key);
        if (data.payment_status === "paid" || data.status === "completed") {
          setState("paid");
          return;
        }
        if (attempt >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch (e) {
        if (process.env.NODE_ENV !== "production") console.error(e);
        setState("error");
      }
    },
    [sessionId],
  );

  useEffect(() => {
    poll(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#FFFDF6] text-slate-900 flex flex-col">
      <SiteHeader cta={{ label: "Back to pricing", to: "/pricing" }} />

      <main className="flex-1 max-w-2xl mx-auto px-5 md:px-8 lg:px-10 py-16 md:py-24 w-full">
        <div
          data-testid="payment-success-card"
          className="border border-slate-900 bg-white p-10 shadow-[8px_8px_0_0_#FFC53D]"
        >
          {state === "polling" && (
            <>
              <Loader2 className="h-10 w-10 text-slate-900 animate-spin mb-6" strokeWidth={1.5} />
              <p className="text-xs tracking-[0.32em] uppercase text-orange-600 font-semibold mb-3">
                Confirming payment
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-4">
                Hang tight — we're verifying with Stripe.
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed">
                This usually takes a few seconds. Please don't close this tab.
              </p>
            </>
          )}

          {state === "paid" && (
            <>
              <CheckCircle2 className="h-10 w-10 text-emerald-600 mb-6" strokeWidth={1.5} />
              <p className="text-xs tracking-[0.32em] uppercase text-emerald-700 font-semibold mb-3">
                Payment confirmed
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-4">
                Welcome to {PLAN_LABELS[lookupKey] || "PressImpact"}.
              </h1>
              <p className="text-base text-slate-600 leading-relaxed mb-8">
                Your subscription is active. A Stripe receipt has been emailed to you.
                Head back to the calculator to run your first premium report.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link to="/calculator">
                  <Button
                    data-testid="success-cta-calculator"
                    className="bg-[#FFC53D] hover:bg-[#FFD866] text-slate-900 rounded-sm px-6 py-5 font-semibold"
                  >
                    Open the calculator
                    <ArrowUpRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-sm px-6 py-5 border-slate-900 text-slate-900"
                  >
                    Back to home
                  </Button>
                </Link>
              </div>
            </>
          )}

          {state === "timeout" && (
            <>
              <Loader2 className="h-10 w-10 text-slate-500 mb-6" strokeWidth={1.5} />
              <p className="text-xs tracking-[0.32em] uppercase text-slate-500 font-semibold mb-3">
                Still processing
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-4">
                Stripe is taking a little longer than usual.
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Your payment may still complete — you'll receive an email from Stripe once
                it does. Refresh this page in a minute, or contact support if the issue persists.
              </p>
              <Button onClick={() => poll(0)} className="rounded-sm bg-slate-900 text-white">
                Check again
              </Button>
            </>
          )}

          {state === "error" && (
            <>
              <XCircle className="h-10 w-10 text-red-600 mb-6" strokeWidth={1.5} />
              <p className="text-xs tracking-[0.32em] uppercase text-red-700 font-semibold mb-3">
                Session not found
              </p>
              <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-4">
                We couldn't confirm this payment.
              </h1>
              <p className="text-sm text-slate-600 leading-relaxed mb-6">
                Try returning to the pricing page and restarting checkout, or email
                <a
                  href="mailto:support@nordicmartech.com"
                  className="underline decoration-[#FFC53D] underline-offset-4 mx-1"
                >
                  support@nordicmartech.com
                </a>
                if you were already charged.
              </p>
              <Link to="/pricing">
                <Button className="rounded-sm bg-slate-900 text-white">
                  Back to pricing
                </Button>
              </Link>
            </>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-[#FFFDF6] text-slate-900 flex flex-col">
      <SiteHeader cta={{ label: "Back to pricing", to: "/pricing" }} />

      <main className="flex-1 max-w-2xl mx-auto px-5 md:px-8 lg:px-10 py-16 md:py-24 w-full">
        <div
          data-testid="payment-cancel-card"
          className="border border-slate-200 bg-white p-10"
        >
          <p className="text-xs tracking-[0.32em] uppercase text-slate-500 font-semibold mb-3">
            Checkout cancelled
          </p>
          <h1 className="font-heading text-3xl md:text-4xl font-light tracking-tight mb-4">
            No charge was made.
          </h1>
          <p className="text-base text-slate-600 leading-relaxed mb-8">
            You cancelled the checkout before completing payment. Nothing has been billed.
            Come back anytime when you're ready to upgrade — the free tier stays available.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/pricing">
              <Button
                data-testid="cancel-cta-pricing"
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm px-6 py-5"
              >
                Back to pricing
                <ArrowUpRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <Link to="/calculator">
              <Button
                variant="outline"
                className="rounded-sm px-6 py-5 border-slate-900 text-slate-900"
              >
                Continue on the free tier
              </Button>
            </Link>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
